import { z } from 'zod';

export const STATUS_CLIENTE = ['ativo', 'inativo', 'falecido', 'suspenso'] as const;
export const statusClienteSchema = z.enum(STATUS_CLIENTE);
export type StatusCliente = z.infer<typeof statusClienteSchema>;

export const ESTADO_CIVIL = [
  'solteiro',
  'casado',
  'divorciado',
  'uniao_estavel',
  'viuvo',
] as const;
export const estadoCivilSchema = z.enum(ESTADO_CIVIL);

export const SEXO = ['M', 'F', 'O'] as const;

// Imóvel resumido — vem aninhado em /api/v2/clientes
export const imovelResumoSchema = z
  .object({
    id: z.union([z.coerce.number(), z.string()]).transform(String),
    denominacao: z.string().nullish(),
    matricula: z.string().nullish(),
    municipio: z.string().nullish(),
    uf: z.string().nullish(),
    area_total_ha: z.coerce.number().nullish(),
    car_codigo: z.string().nullish(),
    tipo_posse: z.string().nullish(),
  })
  .passthrough();
export type ImovelResumo = z.infer<typeof imovelResumoSchema>;


// Contrato/área de arrendamento — vem aninhado em /api/v2/clientes/{cpf}.
// Pode ter imovel_id NULL quando a área é de terceiro e não deve aparecer na aba Imóveis.
// imovel: row inteira da tabela imovel (passthrough — todos os ~80 campos de
// Certidão/CAR/CCIR/Memorial/ITR vêm dentro desse objeto). NULL quando imovel_id é NULL.
export const arrendamentoResumoSchema = z
  .object({
    id: z.union([z.coerce.number(), z.string()]).transform(String),
    imovel_id: z.union([z.coerce.number(), z.string()]).transform(String).nullish(),
    cliente_cpf: z.string().nullish(),
    arrendador_nome: z.string().nullish(),
    arrendatario_nome: z.string().nullish(),
    area_ha: z.coerce.number().nullish(),
    data_inicio: z.string().nullish(),
    data_vencimento: z.string().nullish(),
    status: z.string().nullish(),
    observacoes: z.string().nullish(),
    // legados — back-compat com cards que ainda usam esses campos diretos
    imovel_denominacao: z.string().nullish(),
    imovel_matricula: z.string().nullish(),
    imovel_municipio: z.string().nullish(),
    imovel_uf: z.string().nullish(),
    imoveis: z.array(z.object({ id: z.union([z.coerce.number(), z.string()]).transform(String) }).passthrough()).optional(),
    // imóvel completo (usado pelo ArrendamentoDetailModal pra Certidão/CAR/CCIR/etc)
    imovel: z
      .object({
        id: z.union([z.coerce.number(), z.string()]).transform(String),
      })
      .passthrough()
      .nullish(),
  })
  .passthrough();
export type ArrendamentoResumo = z.infer<typeof arrendamentoResumoSchema>;

// Processo resumido (geo, ambiental, credito) — todos seguem padrão similar
export const processoResumoSchema = z
  .object({
    id: z.union([z.coerce.number(), z.string()]).transform(String),
    tipo_processo: z.string().nullish(),
    status: z.string().nullish(),
    kanban_coluna: z.union([z.coerce.number(), z.string()]).nullish(),
    sla_status: z.string().nullish(),
    imovel_matricula: z.string().nullish(),
    dias_na_coluna: z.number().nullish(),
    imovel_denominacao: z.string().nullish(),
    imovel_municipio: z.string().nullish(),
    responsavel_tecnico_nome: z.string().nullish(),
    pendencias: z.string().nullish(),
    cartorio: z.string().nullish(),
    instituicao: z.string().nullish(),
    programa: z.string().nullish(),
    finalidade: z.string().nullish(),
    valor: z.coerce.number().nullish(),
    valor_aprovado: z.coerce.number().nullish(),
    taxa_juros: z.coerce.number().nullish(),
    prazo_meses: z.coerce.number().nullish(),
    fase_atual: z.string().nullish(),
    card_pipe: z.string().nullish(),
    fase_desde: z.string().nullish(),
    tempo_fase_horas: z.coerce.number().nullish(),
    urgencia: z.string().nullish(),
    responsavel_nome: z.string().nullish(),
    proxima_acao: z.string().nullish(),
    prazo_proxima_acao: z.string().nullish(),
    ultima_movimentacao: z.string().nullish(),
    card_codigo: z.string().nullish(),
    card_url: z.string().nullish(),
    checklist_resumo: z
      .object({
        total: z.number(),
        recebidos: z.number(),
        pendentes: z.number(),
        dispensados: z.number(),
        com_validade_atencao: z.number(),
      })
      .nullish(),
  })
  .passthrough();
export type ProcessoResumo = z.infer<typeof processoResumoSchema>;

export const auditoriaCadastroRowSchema = z.object({
  secao: z.string(),
  entidade: z.string().nullish(),
  campo: z.string(),
  label: z.string(),
  valor_hub: z.string().nullish(),
  valor_documento: z.string().nullish(),
  situacao: z.enum([
    'OK',
    'Falta anexar',
    'Falha de extração',
    'Divergente',
    'Pendente do cliente',
    'Vencido',
  ]),
  acao: z.string(),
  documento_ids: z.array(z.union([z.number(), z.string()])),
});

export const auditoriaCadastroSchema = z.object({
  gerado_em: z.string(),
  cliente: z.object({ cpf: z.string(), nome: z.string() }),
  resumo: z.object({
    campos_analisados: z.number(),
    ja_corretos: z.number(),
    vazios_com_informacao_disponivel: z.number(),
    corrigidos: z.number(),
    divergentes: z.number(),
    realmente_pendentes: z.number(),
    falhas_propagacao: z.number(),
    falta_anexar: z.number(),
    vencidos: z.number(),
    cards_encontrados: z.number(),
    operacoes_sem_vinculo_card: z.number(),
  }),
  rows: z.array(auditoriaCadastroRowSchema),
  cards: z.array(processoResumoSchema),
});
export type AuditoriaCadastro = z.infer<typeof auditoriaCadastroSchema>;

// Item da lista — vindo de /api/v2/clientes (já agregado)
export const clienteListItemSchema = z
  .object({
    cpf: z.string(),
    nome: z.string(),
    municipio: z.string().nullish(),
    uf: z.string().nullish(),
    status: z.string().default('ativo'),
    caf_data_validade: z.string().nullish(),
    caf_situacao: z.string().nullish(),
    score_regularidade: z.coerce.number().nullish(),
    saude_status: z.string().nullish(),
    grupo_economico_id: z.coerce.number().nullish(),
    imoveis: z.array(imovelResumoSchema).default([]),
    arrendamentos: z.array(arrendamentoResumoSchema).default([]),
    processos_geo: z.array(processoResumoSchema).default([]),
    operacoes_credito: z.array(processoResumoSchema).default([]),
  })
  .passthrough();
export type ClienteListItem = z.infer<typeof clienteListItemSchema>;

// Endpoint /api/v2/clientes retorna ARRAY direto (verificado contra
// listClientesComSaude em data-layer.js). Migrado de /api/wenin/clientes
// (legado) em 2026-05-08 — endpoint legado segue ativo no server.js para
// compatibilidade, pode ser removido após confirmar zero consumidores.
export const clientesListSchema = z.array(clienteListItemSchema);

// Detalhe completo — vindo de /api/v2/clientes/{cpf}.
// Schema permissivo: cliente tem 66 colunas, listamos as relevantes pra UI; resto via passthrough.
export const clienteDetailSchema = z
  .object({
    id: z.coerce.number(),
    cpf: z.string(),
    nome: z.string(),
    rg: z.string().nullish(),
    rg_orgao: z.string().nullish(),
    rg_uf_emissor: z.string().nullish(),
    data_nascimento: z.string().nullish(),
    sexo: z.string().nullish(),
    nacionalidade: z.string().nullish(),
    naturalidade: z.string().nullish(),
    uf_nascimento: z.string().nullish(),
    municipio_nascimento: z.string().nullish(),
    profissao: z.string().nullish(),
    escolaridade: z.string().nullish(),
    estado_civil: z.string().nullish(),
    regime_casamento: z.string().nullish(),
    telefone: z.string().nullish(),
    email: z.string().nullish(),
    endereco: z.string().nullish(),
    numero: z.string().nullish(),
    complemento: z.string().nullish(),
    bairro: z.string().nullish(),
    cep: z.string().nullish(),
    municipio: z.string().nullish(),
    uf: z.string().nullish(),
    filiacao_pai: z.string().nullish(),
    filiacao_pai_cpf: z.string().nullish(),
    filiacao_mae: z.string().nullish(),
    filiacao_mae_cpf: z.string().nullish(),
    conjuge_nome: z.string().nullish(),
    conjuge_cpf: z.string().nullish(),
    conjuge_rg: z.string().nullish(),
    conjuge_data_nascimento: z.string().nullish(),
    conjuge_naturalidade: z.string().nullish(),
    conjuge_profissao: z.string().nullish(),
    caf_ufpa_numero: z.string().nullish(),
    caf_situacao: z.string().nullish(),
    caf_data_validade: z.string().nullish(),
    caf_data_inscricao: z.string().nullish(),
    caf_enquadramento_pronaf: z.string().nullish(),
    caf_area_estabelecimento_ha: z.coerce.number().nullish(),
    renda_bruta_anual: z.coerce.number().nullish(),
    renda_liquida_anual: z.coerce.number().nullish(),
    status: z.string().default('ativo'),
    data_inativacao: z.string().nullish(),
    motivo_inativacao: z.string().nullish(),
    observacoes: z.string().nullish(),
    score_regularidade: z.coerce.number().nullish(),
    grupo_economico_id: z.coerce.number().nullish(),
    orcamento_payload: z.unknown().nullish(),
    imoveis: z.array(imovelResumoSchema).default([]),
    arrendamentos: z.array(arrendamentoResumoSchema).default([]),
    processos_geo: z.array(processoResumoSchema).default([]),
    operacoes_credito: z.array(processoResumoSchema).default([]),
    created_at: z.string().nullish(),
    updated_at: z.string().nullish(),
  })
  .passthrough();
export type ClienteDetail = z.infer<typeof clienteDetailSchema>;

// Atualização — todos os campos opcionais
export const clienteUpdateSchema = clienteDetailSchema
  .partial()
  .pick({
    nome: true,
    rg: true,
    rg_orgao: true,
    rg_uf_emissor: true,
    data_nascimento: true,
    sexo: true,
    nacionalidade: true,
    naturalidade: true,
    escolaridade: true,
    profissao: true,
    renda_bruta_anual: true,
    estado_civil: true,
    regime_casamento: true,
    telefone: true,
    email: true,
    endereco: true,
    numero: true,
    complemento: true,
    bairro: true,
    cep: true,
    municipio: true,
    uf: true,
    filiacao_pai: true,
    filiacao_pai_cpf: true,
    filiacao_mae: true,
    filiacao_mae_cpf: true,
    conjuge_nome: true,
    conjuge_cpf: true,
    conjuge_data_nascimento: true,
    conjuge_naturalidade: true,
    status: true,
    data_inativacao: true,
    motivo_inativacao: true,
    observacoes: true,
    grupo_economico_id: true,
    orcamento_payload: true,
  });
export type ClienteUpdateInput = z.infer<typeof clienteUpdateSchema>;

// Processos ambiental (lazy load no resumo)
export const processosAmbientalSchema = z.array(
  z
    .object({
      id: z.union([z.coerce.number(), z.string()]).transform(String),
      tipo_processo: z.string().nullish(),
      status: z.string().nullish(),
      kanban_coluna: z.union([z.coerce.number(), z.string()]).nullish(),
      data_validade_licenca: z.string().nullish(),
      codigo_car: z.string().nullish(),
      imovel_matricula: z.string().nullish(),
    })
    .passthrough(),
);
export type ProcessoAmbiental = z.infer<typeof processosAmbientalSchema>[number];

export const okSchema = z.object({ ok: z.boolean().optional(), deleted: z.boolean().optional() });

// Bens & Direitos (IRPF) — vem de /api/v2/clientes/{cpf}/bens-direitos
export const bemDireitoSchema = z
  .object({
    id: z.union([z.coerce.number(), z.string()]).transform(String).nullish(),
    codigo: z.string().nullish(),
    grupo: z.string().nullish(),
    ano_calendario: z.union([z.coerce.number(), z.string()]).nullish(),
    discriminacao: z.string().nullish(),
    valor_ano_atual: z.coerce.number().nullish(),
    valor_ano_anterior: z.coerce.number().nullish(),
    matricula: z.string().nullish(),
    cib: z.string().nullish(),
    municipio: z.string().nullish(),
    uf: z.string().nullish(),
    data_aquisicao: z.string().nullish(),
    area_total_ha: z.coerce.number().nullish(),
    cartorio: z.string().nullish(),
    imovel_id: z.union([z.coerce.number(), z.string()]).transform(String).nullish(),
  })
  .passthrough();
export type BemDireito = z.infer<typeof bemDireitoSchema>;

export const bensDireitosSchema = z.array(bemDireitoSchema);

// Imóvel — schema completo para CRUD
export const imovelFullSchema = z
  .object({
    id: z.union([z.coerce.number(), z.string()]).transform(String),
    proprietario_cpf: z.string().nullish(),
    denominacao: z.string().nullish(),
    matricula: z.string().nullish(),
    municipio: z.string().nullish(),
    uf: z.string().nullish(),
    area_total_ha: z.coerce.number().nullish(),
    area_lavoura_ha: z.coerce.number().nullish(),
    car_codigo: z.string().nullish(),
    ccir_status: z.string().nullish(),
    ccir_ano: z.union([z.coerce.number(), z.string()]).nullish(),
    itr_status: z.string().nullish(),
    created_at: z.string().nullish(),
    updated_at: z.string().nullish(),
  })
  .passthrough();
export type ImovelFull = z.infer<typeof imovelFullSchema>;

export const imovelCreateSchema = z.object({
  proprietario_cpf: z.string().min(11, 'CPF do proprietário obrigatório'),
  denominacao: z.string().min(2, 'Denominação muito curta'),
  matricula: z.string().optional().or(z.literal('')),
  municipio: z.string().optional().or(z.literal('')),
  uf: z.string().max(2).optional().or(z.literal('')),
  area_total_ha: z.coerce.number().nullish(),
  area_lavoura_ha: z.coerce.number().nullish(),
  car_codigo: z.string().optional().or(z.literal('')),
});
export type ImovelCreateInput = z.infer<typeof imovelCreateSchema>;

// Permissivo: PATCH /api/v2/imoveis/:id aceita qualquer subset das colunas
// existentes em `imovel` (back valida via getImovelCols). Inline edit precisa
// disso pra mandar campos como cib, nirf, ccir_*, car_*, itr_*, etc.
export const imovelUpdateSchema = z.record(z.unknown());
export type ImovelUpdateInput = z.infer<typeof imovelUpdateSchema>;

// Documentos do cliente — endpoint pode retornar formato variado, aceitamos com passthrough
export const documentoSchema = z
  .object({
    id: z.union([z.coerce.number(), z.string()]).transform(String).nullish(),
    tipo: z.string().nullish(),
    secao: z.string().nullish(),
    arquivo: z.string().nullish(),
    arquivo_url: z.string().nullish(),
    validade: z.string().nullish(),
    status: z.string().nullish(),
    created_at: z.string().nullish(),
    cliente_cpf: z.string().nullish(),
    imovel_id: z.union([z.coerce.number(), z.string()]).transform(String).nullish(),
    imovel_denominacao: z.string().nullish(),
    imovel_matricula: z.string().nullish(),
    imovel_municipio: z.string().nullish(),
    imovel_uf: z.string().nullish(),
  })
  .passthrough();
export type Documento = z.infer<typeof documentoSchema>;

// O endpoint /api/v2/clientes/{cpf} pode embutir documentos[] ou expor em chamada própria.
// Aceitamos as duas formas no parsing (extraímos do detalhe via uma função helper).
export const uploadDocResponseSchema = z.object({
  success: z.boolean().optional(),
  ok: z.boolean().optional(),
  arquivo_id: z.union([z.coerce.number(), z.string()]).optional(),
});

// Enriquecer (16 integrações com polling)
export const enriquecerStartSchema = z.object({
  job_id: z.string().optional(),
  status: z.string().optional(),
});

export const enriquecerProgressoSchema = z.object({
  progresso: z.coerce.number().default(0),
  status: z.string().nullish(),
  messages: z.array(z.string()).default([]),
  concluido: z.boolean().default(false),
}).passthrough();

export const enriquecerPreviewSchema = z
  .object({
    preview: z.record(z.unknown()).default({}),
  })
  .passthrough();

export const enriquecerAplicarSchema = z.object({
  applied: z.boolean().optional(),
  fields_updated: z.array(z.string()).optional(),
}).passthrough();
